import { useCallback, useEffect, useState } from "react";

import { showSuccessMsg } from "../../../../../utils/Helpers";
import { updateEventAutoDeduction } from "../../../../../network/apis/events";
import { updateCompanyPortalEventAutoDeduction } from "../../../../../network/apis/companyPortalEvents";

/**
 * PROLINE-3179 — event-level automatic-deduction flags.
 * Both flags are read-only in the event response and written only through
 * `PATCH /events/:id/update_auto_deduction` (mirrored in both portals).
 */
const useEventPayrollAutoDeduction = ({
  event,
  portal = "admin",
  successMessage = "",
  canEdit = true,
  onSuccess,
} = {}) => {
  const eventId = event?.id;
  const [lateCheckInDeduction, setLateCheckInDeduction] = useState(
    !!event?.late_checkin_deduction,
  );
  const [autoCheckout, setAutoCheckout] = useState(
    !!event?.automatic_checkout_deduction,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLateCheckInDeduction(!!event?.late_checkin_deduction);
    setAutoCheckout(!!event?.automatic_checkout_deduction);
  }, [eventId, event?.late_checkin_deduction, event?.automatic_checkout_deduction]);

  const updateFlag = useCallback(
    async (key, value, applyLocally) => {
      // The toggles are already disabled without the payroll edit permission —
      // this guard keeps the PATCH unreachable if a caller wires them up anyway.
      if (!eventId || !canEdit) return;

      const updateApi =
        portal === "company"
          ? updateCompanyPortalEventAutoDeduction
          : updateEventAutoDeduction;

      applyLocally(value);
      setSaving(true);
      try {
        const response = await updateApi({
          id: eventId,
          params: { [key]: value },
        });
        // Local copy is preferred: it also states that only future actions are affected.
        showSuccessMsg(successMessage || response?.data?.message, true);
        // refresh the list to reflect it.
        onSuccess?.();
      } catch {
        // Interceptors surface the error message; revert the optimistic toggle.
        applyLocally(!value);
      } finally {
        setSaving(false);
      }
    },
    [eventId, portal, successMessage, canEdit, onSuccess],
  );

  const handleLateCheckInDeductionChange = useCallback(
    (value) => updateFlag("late_checkin_deduction", value, setLateCheckInDeduction),
    [updateFlag],
  );

  const handleAutoCheckoutChange = useCallback(
    (value) =>
      updateFlag("automatic_checkout_deduction", value, setAutoCheckout),
    [updateFlag],
  );

  return {
    lateCheckInDeduction,
    autoCheckout,
    saving,
    onLateCheckInDeductionChange: handleLateCheckInDeductionChange,
    onAutoCheckoutChange: handleAutoCheckoutChange,
  };
};

export default useEventPayrollAutoDeduction;
